import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import axios, { API, authed, errMsg, photoSrc } from "../../lib/api";

const EMPTY = { name: "", bio: "", photo_url: "", active: true };

export const WasherForm = ({ washer, open, onOpenChange, onSaved }) => {
  const { getToken } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(washer ? { name: washer.name, bio: washer.bio, photo_url: washer.photo_url, active: washer.active } : EMPTY);
  }, [open, washer]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/upload`, fd, await authed(getToken));
      setForm((f) => ({ ...f, photo_url: r.data.photo_url }));
    } catch (e) { toast.error(errMsg(e, "Poza nu a putut fi încărcată.")); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cfg = await authed(getToken);
      if (washer) await axios.put(`${API}/admin/washers/${washer.id}`, form, cfg);
      else await axios.post(`${API}/admin/washers`, form, cfg);
      toast.success(washer ? "Spălătorul a fost actualizat." : "Spălătorul a fost adăugat.");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-[#131B26]" data-testid="washer-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{washer ? "Editează spălătorul" : "Adaugă spălător nou"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <label className="flex cursor-pointer items-center gap-4" data-testid="washer-photo-upload-label">
            <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl border border-dashed border-white/15 bg-white/5">
              {uploading ? <Loader2 className="animate-spin" /> : form.photo_url
                ? <img src={photoSrc(form.photo_url)} alt="" className="h-full w-full object-cover" data-testid="washer-photo-preview" />
                : <ImagePlus className="text-slate-500" />}
            </div>
            <span className="text-sm text-slate-300">{form.photo_url ? "Schimbă poza" : "Încarcă o poză"}</span>
            <input type="file" accept="image/*" className="hidden" data-testid="washer-photo-input"
              onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          <Input required data-testid="washer-name-input" placeholder="Nume" value={form.name} maxLength={80}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Textarea data-testid="washer-bio-input" placeholder="Scurtă descriere" rows={3} value={form.bio} maxLength={300}
            onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          <label className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3">
            <span className="text-sm">Activ (vizibil clienților)</span>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="washer-active-switch" />
          </label>
          <button type="submit" disabled={busy || uploading} data-testid="washer-save-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#00F0FF] py-3 font-semibold text-[#090E17] hover:bg-[#33F3FF] disabled:opacity-60">
            {busy && <Loader2 className="animate-spin" size={18} />} Salvează
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
